import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../modal'
import { Button } from '../button'
import { Input } from '../input'
import { CustomSelect } from '../custom-select'
import { expenseApi } from 'src/services/expenses/api'
import { expenseQueries } from 'src/services/expenses/queries'
import { ExpenseGroup } from 'src/services/expenses/models'
import { useToast } from '../toast'
import { extractErrorMessage } from 'src/utils/errors'
import { cn } from 'src/utils/style'
import { organizationQueries } from 'src/services/user'
import { expenseReportQueries } from 'src/services/expense-reports/queries'
import { SelectOption } from 'src/services/base-model'
import { usePermissions } from 'src/hooks/use-permissions'

type AmountMode = 'dollar' | 'percentage'

interface CopyAndSplitModalProps {
  isOpen: boolean
  onClose: () => void
  expense: ExpenseGroup
}

export const CopyAndSplitModal: React.FC<CopyAndSplitModalProps> = ({
  isOpen,
  onClose,
  expense,
}) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { hasPermission } = usePermissions()
  const canAccessReports = hasPermission('reports.access')

  const originalAmount = Number(expense.amount)

  const [amountMode, setAmountMode] = useState<AmountMode>('dollar')
  const [dollarValue, setDollarValue] = useState('')
  const [percentageValue, setPercentageValue] = useState('')
  const [name, setName] = useState(`${expense.name} ${t('copySplit.nameSuffix')}`)
  const [notes, setNotes] = useState(expense.notes ?? '')
  const [copyReceipts, setCopyReceipts] = useState(true)

  const [orgSearch, setOrgSearch] = useState('')
  const [reportSearch, setReportSearch] = useState('')

  const [selectedOrg, setSelectedOrg] = useState<SelectOption[]>(
    expense.organizationRef
      ? [{ label: expense.organizationRef.name, value: expense.organizationRef.id }]
      : [],
  )

  const [selectedReports, setSelectedReports] = useState<SelectOption[]>(
    expense.reportsRef?.map((r) => ({ label: r.name, value: r.id })) ?? [],
  )

  const { data: organizations } = useQuery(
    organizationQueries.list({ filters: { search: orgSearch } }),
  )
  const { data: reports } = useQuery({
    ...expenseReportQueries.list({ filters: { search: reportSearch } }),
    enabled: canAccessReports,
  })

  const orgOpts = useMemo(
    () => organizations?.results.map((o) => ({ label: o.name, value: o.id })) ?? [],
    [organizations?.results],
  )

  const reportOpts = useMemo(
    () =>
      reports?.results.map((r) => ({
        label: `${r.name} - ${r.organizationRef?.name ?? ''}`,
        value: r.id,
      })) ?? [],
    [reports?.results],
  )

  const splitAmount = useMemo(() => {
    if (amountMode === 'dollar') {
      return parseFloat(dollarValue) || 0
    }
    const pct = parseFloat(percentageValue) || 0
    return (pct / 100) * originalAmount
  }, [amountMode, dollarValue, percentageValue, originalAmount])

  const displayPercentage = useMemo(() => {
    if (amountMode === 'percentage') return percentageValue
    if (!dollarValue || originalAmount === 0) return ''
    return ((splitAmount / originalAmount) * 100).toFixed(2)
  }, [amountMode, percentageValue, dollarValue, splitAmount, originalAmount])

  const displayDollar = useMemo(() => {
    if (amountMode === 'dollar') return dollarValue
    return splitAmount > 0 ? splitAmount.toFixed(2) : ''
  }, [amountMode, dollarValue, splitAmount])

  const amountError = useMemo(() => {
    if (!dollarValue && !percentageValue) return null
    if (splitAmount <= 0) return t('copySplit.amountGreaterThanZero')
    if (splitAmount >= originalAmount) return t('copySplit.amountLessThanOriginal')
    return null
  }, [splitAmount, originalAmount, dollarValue, percentageValue, t])

  const handleDollarChange = (val: string) => {
    setDollarValue(val)
    if (val && originalAmount > 0) {
      const pct = (parseFloat(val) / originalAmount) * 100
      setPercentageValue(pct > 0 ? pct.toFixed(2) : '')
    } else {
      setPercentageValue('')
    }
  }

  const handlePercentageChange = (val: string) => {
    setPercentageValue(val)
    if (val && originalAmount > 0) {
      const amt = (parseFloat(val) / 100) * originalAmount
      setDollarValue(amt > 0 ? amt.toFixed(2) : '')
    } else {
      setDollarValue('')
    }
  }

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: expense.amountCurrency,
  })

  const { mutate: submit, isPending } = useMutation({
    mutationFn: () =>
      expenseApi.csc.copyAndSplit({
        id: expense.id,
        amount: splitAmount,
        name,
        organization: selectedOrg[0]?.value,
        reports: selectedReports.map((r) => r.value),
        notes,
        copyReceipts,
      }),
    onSuccess: () => {
      toast({ description: t('copySplit.splitSuccess'), type: 'success' })
      queryClient.invalidateQueries({ queryKey: expenseQueries.all() })
      onClose()
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: extractErrorMessage(error), type: 'error' })
    },
  })

  const canSubmit = splitAmount > 0 && splitAmount < originalAmount && !isPending

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('copySplit.title')} size="md">
      <div className="space-y-4">
        {/* Original expense summary */}
        <div className="rounded-md bg-surface-hover p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-text-secondary">{t('copySplit.originalExpense')}</span>
            <span className="font-medium">{expense.name}</span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-text-secondary">{t('copySplit.amount')}</span>
            <span className="font-medium">{currencyFormatter.format(originalAmount)}</span>
          </div>
        </div>

        {/* Amount mode toggle */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">
            {t('copySplit.splitAmount')}
          </label>
          <div className="flex rounded-md border border-border">
            <button
              type="button"
              className={cn(
                'flex-1 rounded-l-md px-3 py-2 text-sm transition-colors',
                amountMode === 'dollar'
                  ? 'bg-primary text-white'
                  : 'bg-surface text-text-secondary hover:bg-surface-hover',
              )}
              onClick={() => setAmountMode('dollar')}
            >
              {t('copySplit.dollar')}
            </button>
            <button
              type="button"
              className={cn(
                'flex-1 rounded-r-md px-3 py-2 text-sm transition-colors',
                amountMode === 'percentage'
                  ? 'bg-primary text-white'
                  : 'bg-surface text-text-secondary hover:bg-surface-hover',
              )}
              onClick={() => setAmountMode('percentage')}
            >
              {t('copySplit.percentage')}
            </button>
          </div>
        </div>

        {/* Amount inputs */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-primary">
              {t('copySplit.amountWithCurrency', { currency: expense.amountCurrency })}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={displayDollar}
              onChange={(e) => handleDollarChange(e.target.value)}
              disabled={amountMode === 'percentage'}
              className={cn(
                'w-full rounded-md border bg-surface p-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-border-strong',
                amountMode === 'percentage' && 'cursor-not-allowed opacity-60',
                amountError && 'border-error-400 ring-2 ring-error-400',
              )}
              placeholder="0.00"
              data-testid="split-amount-dollar"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-primary">
              {t('copySplit.percentage')}
            </label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={displayPercentage}
                onChange={(e) => handlePercentageChange(e.target.value)}
                disabled={amountMode === 'dollar'}
                className={cn(
                  'w-full rounded-md border bg-surface p-2 pr-8 text-text-primary focus:outline-none focus:ring-2 focus:ring-border-strong',
                  amountMode === 'dollar' && 'cursor-not-allowed opacity-60',
                  amountError && 'border-error-400 ring-2 ring-error-400',
                )}
                placeholder="0"
                data-testid="split-amount-percentage"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">
                %
              </span>
            </div>
          </div>
        </div>
        {amountError && (
          <p className="text-sm text-error" data-testid="split-amount-error">
            {amountError}
          </p>
        )}

        {/* Split preview */}
        {splitAmount > 0 && !amountError && (
          <div className="rounded-md border border-border-strong bg-surface-hover p-3 text-sm">
            <div className="flex justify-between">
              <span>{t('copySplit.originalAfterSplit')}</span>
              <span className="font-medium">
                {currencyFormatter.format(originalAmount - splitAmount)}
              </span>
            </div>
            <div className="mt-1 flex justify-between">
              <span>{t('copySplit.newExpense')}</span>
              <span className="font-medium">{currencyFormatter.format(splitAmount)}</span>
            </div>
          </div>
        )}

        {/* Name */}
        <div>
          <Input
            label={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="split-name"
          />
        </div>

        {/* Organization */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">
            {t('expenseForm.organizationLabel')}
          </label>
          <CustomSelect
            searchable={true}
            multi={false}
            clearable={true}
            options={orgOpts}
            values={selectedOrg}
            onChange={(values) => setSelectedOrg(values)}
            searchFn={({ state }) => {
              setOrgSearch(state.search)
              return orgOpts
            }}
          />
        </div>

        {/* Reports */}
        {canAccessReports && (
          <div>
            <label className="mb-1 block text-sm font-medium text-text-primary">
              {t('expenseForm.reports')}
            </label>
            <CustomSelect
              searchable={true}
              multi={true}
              clearable={true}
              options={reportOpts}
              values={selectedReports}
              onChange={(values) => setSelectedReports(values)}
              searchFn={({ state }) => {
                setReportSearch(state.search)
                return reportOpts
              }}
            />
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary">
            {t('common.notes')}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-md border border-border bg-surface p-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-border-strong"
            rows={3}
            data-testid="split-notes"
          />
        </div>

        {/* Copy receipts toggle */}
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={copyReceipts}
            onChange={(e) => setCopyReceipts(e.target.checked)}
            data-testid="split-copy-receipts"
          />
          <span className="text-sm">{t('copySplit.copyReceipts')}</span>
        </label>

        {/* Actions */}
        <div className="flex justify-end space-x-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => submit()}
            disabled={!canSubmit}
            data-testid="split-submit"
          >
            {isPending ? t('copySplit.splitting') : t('copySplit.split')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
